import { BadgeCheck, BarChart3, CalendarDays, MapPin, Pencil, UserPlus, Users } from 'lucide-react'
import { OrgPlanFeatureRow } from '@/shared/ui/components'
import type { OrgPlanFeatureItem } from '@/shared/ui/components'
import { Button } from '@/shared/ui/primitives'
import { CrewHeroCard } from './crew-hero-card'

export type CrewExplainerStepProps = {
  isContinuing: boolean
  message: string
  onContinue: () => void
  fullBleed?: boolean
}

const WHAT_A_CREW_IS: OrgPlanFeatureItem[] = [
  {
    icon: <Users className="size-5" />,
    title: 'Your Hunting Group',
    description: 'A private group for the hunters you hunt with',
  },
  {
    icon: <BarChart3 className="size-5" />,
    title: 'Shared Hunt Log',
    description: 'Everyone logs hunts and harvests to one crew log',
  },
  {
    icon: <CalendarDays className="size-5" />,
    title: 'Hunt Calendar',
    description: "Schedule hunts and see who's going",
  },
  {
    icon: <MapPin className="size-5" />,
    title: 'Shared Properties',
    description: "Your crew's properties, blinds, and spots in one place",
  },
]

const HOW_IT_WORKS: OrgPlanFeatureItem[] = [
  {
    icon: <Pencil className="size-5" />,
    title: '1. Name your crew',
    description: "Creating a crew is free, and you're its first member",
  },
  {
    icon: <UserPlus className="size-5" />,
    title: '2. Invite your buddies',
    description: 'Anyone in the crew can invite more hunters',
  },
  {
    icon: <BadgeCheck className="size-5" />,
    title: '3. Everyone hunts on Pro',
    description:
      'No seats to buy. Each member needs their own BlindBook Pro, and invitees without it are asked to upgrade when they join',
  },
]

export function CrewExplainerStep({
  isContinuing,
  message,
  onContinue,
  fullBleed = false,
}: CrewExplainerStepProps) {
  return (
    <CrewHeroCard
      testId="step-crewExplainer"
      title="Start a Hunt Crew"
      subtitle="A crew is a group of hunters who log hunts and plan together. Creating a crew is free, but every crew member needs BlindBook Pro."
      fullBleed={fullBleed}
    >
      {message ? (
        <p role="alert" data-testid="crew-explainer-message" className="text-sm text-destructive">
          {message}
        </p>
      ) : null}

      <Button
        type="button"
        onClick={onContinue}
        disabled={isContinuing}
        data-testid="crew-explainer-continue"
        className="h-auto w-full rounded-lg py-3.5 text-base font-semibold"
      >
        Continue
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Free to create · Every member needs BlindBook Pro
      </p>

      <div className="mt-4">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.16em] text-primary">
          What a Crew Is
        </p>
        <div className="flex flex-col gap-4">
          {WHAT_A_CREW_IS.map((item) => (
            <OrgPlanFeatureRow key={item.title} feature={item} comingSoon={false} />
          ))}
        </div>
      </div>

      <div>
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.16em] text-primary">
          How It Works
        </p>
        <div className="flex flex-col gap-4">
          {HOW_IT_WORKS.map((item) => (
            <OrgPlanFeatureRow key={item.title} feature={item} comingSoon={false} />
          ))}
        </div>
      </div>
    </CrewHeroCard>
  )
}
